import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'components/ui/Button';
import { useSidebar } from 'contexts/SidebarContext';
import { cn } from 'lib/utils';

interface ChatInputProps {
  inputValue: string;
  setInputValue: (value: string) => void;
  onSendMessage: (message: string) => void;
  isLoading: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputValue,
  setInputValue,
  onSendMessage,
  isLoading,
}) => {
  const [localValue, setLocalValue] = useState(inputValue);
  const { isCollapsed, isMobile } = useSidebar();
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get('conversation');

  React.useEffect(() => {
    setLocalValue(inputValue);
  }, [inputValue]);

  // Clear input when conversation query parameter changes
  React.useEffect(() => {
    setLocalValue('');
    setInputValue('');
  }, [conversationId, setInputValue]);

  const handleSendMessage = () => {
    if (!localValue.trim() || isLoading) return;

    const message = localValue.trim();
    setLocalValue('');
    setInputValue('');
    onSendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
    setInputValue(e.target.value);
  };

  return (
    <div
      className={cn(
        'fixed bottom-0 right-0 transition-all duration-100 ease-in-out',
        'border-t border-gray-200 bg-white p-4',
        isMobile ? 'left-0' : isCollapsed ? 'left-[80px]' : 'left-[280px]',
      )}
    >
      <div className="w-full mx-auto">
        <div className="relative flex flex-col gap-2">
          <textarea
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you need..."
            rows={3}
            disabled={isLoading}
            className="flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-black placeholder:text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 pr-28"
          />
          <Button
            onClick={handleSendMessage}
            disabled={!localValue.trim() || isLoading}
            loading={isLoading}
            variant="primary"
            size="sm"
            className="absolute z-10 w-fit right-3 bottom-3 px-6 py-2"
          >
            <span className="text-sm">Send</span>
          </Button>
          {/* </div> */}
        </div>
        <p className="text-xs sr-only text-gray-500 mt-1">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default ChatInput;
